import { formatCountdown } from '../../i18n/format'
import { useTranslation } from '../../i18n/I18nProvider'
import { Bidi } from '../Bidi'
import { PrimaryButton, LinkButton } from '../Button'
import { AuthError } from './AuthError'
import type { CodeStepProps } from './types'

const OTP_CODE_LENGTH = 6

/**
 * OTP entry (§7.9). The mock verifier and its demo constant are gone — verification is
 * `web/src/auth/`'s call to P2's Cognito custom auth flow; this component only reports what
 * the caller told it (`error`, `verifying`) and never judges the code itself.
 */
export function CodeStep({
  phone,
  code,
  onChangeCode,
  error,
  onVerify,
  verifying,
  resendInSeconds,
  onResend,
  onChangeNumber,
}: CodeStepProps): JSX.Element {
  const { t } = useTranslation('auth')

  const canSubmit = code.length === OTP_CODE_LENGTH && !verifying

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        if (canSubmit) onVerify(code)
      }}
    >
      <div style={{ padding: '36px 16px 4px', textAlign: 'center' }}>
        <div style={{ fontSize: 'var(--font-header)', fontWeight: 700, marginBlockEnd: 6 }}>
          {t('codeTitle')}
        </div>
        <div style={{ fontSize: 'var(--font-control)', color: 'var(--muted)' }}>
          {t('codeSentTo')} <Bidi>{phone}</Bidi>
        </div>
      </div>
      <div className="form-row">
        <label htmlFor="auth-code">{t('codeLabel')}</label>
        <input
          id="auth-code"
          className="input mono"
          value={code}
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder={t('codePlaceholder')}
          aria-label={t('codeLabel')}
          disabled={verifying}
          onChange={(event) => {
            const nextCode = event.target.value.replace(/\D/g, '').slice(0, OTP_CODE_LENGTH)
            onChangeCode(nextCode)
            if (nextCode.length === OTP_CODE_LENGTH && !verifying) onVerify(nextCode)
          }}
        />
      </div>
      <AuthError error={error} />
      <div className="hint centered">
        {resendInSeconds > 0 ? (
          <>
            {t('resendIn', { time: '' })}
            <Bidi>{formatCountdown(resendInSeconds)}</Bidi>
          </>
        ) : (
          <LinkButton onClick={onResend}>{t('resendNow')}</LinkButton>
        )}
      </div>
      {/* Wrapped in `.form-row` (fix A) so the button's inline inset matches the input's above
          it — `.btn-primary` alone is edge-to-edge and bled past the input's 16px inset. */}
      <div className="form-row">
        <PrimaryButton type="submit" disabled={!canSubmit}>
          {t('verifyAndContinue')}
        </PrimaryButton>
      </div>
      <div className="hint centered">
        <LinkButton onClick={onChangeNumber}>{t('changeNumber')}</LinkButton>
      </div>
    </form>
  )
}
